import React from 'react'

import { format, isValid, parse } from 'date-fns'
import { enUS, ja } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@enechain/ecloud-designsystem'

import { deriveMarginReportEntry } from './derive'
import { formatOkuValue, toOkuValue } from './format'
import { MarginReportChartInput } from './types'

const COLOR_TEXT_HIGH = 'rgba(0, 0, 0, 0.87)'
const COLOR_TEXT_MID = 'rgba(0, 0, 0, 0.62)'

// ラベル＋値の同型行。ツールチップ内の「セクション見出し／内訳」行はすべてこの形。
const TooltipRow = ({
  label,
  value,
  alignItems = 'flex-start',
  gap,
  labelColor = COLOR_TEXT_HIGH,
  labelWeight,
  valueColor = COLOR_TEXT_HIGH,
  borderBottomWidth,
  borderBottomColor,
}: {
  label: string
  value: string
  alignItems?: 'center' | 'flex-start'
  gap?: string
  labelColor?: string
  labelWeight?: string
  valueColor?: string
  borderBottomWidth?: string
  borderBottomColor?: string
}): React.ReactNode => (
  <Flex
    gap={gap}
    justifyContent="space-between"
    alignItems={alignItems}
    width="100%"
    borderBottomWidth={borderBottomWidth}
    borderBottomColor={borderBottomColor}
  >
    <Text
      fontSize="12px"
      color={labelColor}
      fontWeight={labelWeight}
      whiteSpace="nowrap"
    >
      {label}
    </Text>
    <Text
      fontSize="14px"
      color={valueColor}
      fontWeight="600"
      whiteSpace="nowrap"
    >
      {value}
    </Text>
  </Flex>
)

// 点線コネクタ付きの行（不足・余剰金（返金可能）で使用）
const OutlinedRow = ({
  label,
  value,
  valueColor = 'Text.highEmphasis',
}: {
  label: string
  value: string
  valueColor?: string
}): React.ReactNode => (
  <Flex
    alignItems="center"
    width="100%"
    borderLeftWidth="2px"
    borderLeftStyle="dotted"
    borderLeftColor="Border.midEmphasis"
  >
    <Box
      height="1px"
      width="8px"
      backgroundColor="Border.midEmphasis"
      flexShrink="0"
    />
    <Box
      borderLeftWidth="2px"
      borderLeftStyle="solid"
      borderLeftColor="Border.neutralMid"
      padding="4px 8px"
      flex="1"
      display="flex"
      alignItems="flex-end"
      justifyContent="flex-end"
      minHeight="1px"
      minWidth="1px"
    >
      <Flex justifyContent="space-between" alignItems="center" width="100%">
        <Text
          fontSize="12px"
          color={COLOR_TEXT_HIGH}
          fontWeight="500"
          whiteSpace="nowrap"
        >
          {label}
        </Text>
        <Text
          fontSize="14px"
          color={valueColor}
          fontWeight="700"
          whiteSpace="nowrap"
        >
          {value}
        </Text>
      </Flex>
    </Box>
  </Flex>
)

export const MarginReportTooltip = ({
  active,
  payload,
  label,
  isHybrid,
}: {
  active?: boolean
  payload?: Array<{
    payload?: MarginReportChartInput
  }>
  label?: string
  isHybrid: boolean
}): React.ReactNode => {
  const { t, i18n } = useTranslation(['component'])
  const okuDivisor = i18n.language === 'ja' ? 1e8 : 1e6
  const fractionDigits = i18n.language === 'ja' ? 1 : 0

  if (active !== true || payload === undefined || payload.length === 0) {
    return null
  }

  // 数値フォーマット（億単位で小数点1桁）。null は「—」、それ以外は「X.X億」
  // minUnit を指定すると、0 < value の場合に最小値を保証する（例: 未精算代金は 0.1 億未満を 0.1 億表示）
  const formatValue = (value: number | null, minUnit?: number): string => {
    if (value == null) {
      return '—'
    }
    const unitValue = toOkuValue(value, okuDivisor)
    const display =
      minUnit !== undefined && value > 0 && unitValue < minUnit
        ? minUnit
        : unitValue
    return `¥${formatOkuValue(display, t('component:features.margin_report.oku_unit'), fractionDigits)}`
  }

  const formatUnsettledAmount = (value: number | null): string =>
    formatValue(value, 1e7 / okuDivisor)

  // データはすべて recharts の datum（= MarginReportChartInput）から読む
  const datum = payload[0]?.payload
  const deposits = datum?.deposits ?? null
  const unrealizedAmount = datum?.unrealizedAmount ?? null
  const realizedAmount = datum?.realizedAmount ?? null
  const requiredMargin = datum?.requiredMargin ?? null
  const initialMargin = datum?.initialMargin ?? null
  const surplus = datum?.surplus ?? null
  const unsettledAmount = datum?.materializedUnsettledAmount ?? null
  const totalCreditAmount = datum?.totalCreditAmount ?? null
  const performanceGuarantee = datum?.performanceGuarantee ?? null

  // 不足・余剰金（返金可能）は derive.ts の共通ロジックで算出する（Tooltip 内では再計算しない）
  const derived =
    datum != null ? deriveMarginReportEntry(datum, isHybrid) : null
  const shortage = derived?.shortage ?? null
  const refundableSurplus = derived?.refundableSurplus ?? null

  // すべての値が欠損している場合はツールチップを表示しない
  if (
    deposits == null &&
    unrealizedAmount == null &&
    realizedAmount == null &&
    requiredMargin == null &&
    initialMargin == null &&
    surplus == null &&
    totalCreditAmount == null &&
    performanceGuarantee == null
  ) {
    return null
  }

  // 日付ラベルのフォーマット（不正値は '-' にフォールバック）
  const formattedLabel = ((): string => {
    if (label == null || label === '') {
      return '-'
    }
    const date = parse(label, 'yyyy-MM-dd', new Date())
    if (!isValid(date)) {
      return '-'
    }
    const dateLocale = i18n.language === 'ja' ? ja : enUS
    const dateFormat =
      i18n.language === 'ja' ? 'yyyy/M/d（E）' : 'yyyy/M/d (EEE)'
    return format(date, dateFormat, { locale: dateLocale })
  })()

  // 小計 = 再構築コスト + 未精算代金
  const subtotal =
    initialMargin != null && unsettledAmount != null
      ? initialMargin + unsettledAmount
      : null

  return (
    <Flex gap="2" alignItems="center">
      {/* メイン詳細パネル */}
      <Box
        backgroundColor="rgba(255, 255, 255, 0.96)"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="Border.midEmphasis"
        borderRadius="radiiBase"
        padding="2"
        minWidth="250px"
        width="max-content"
        display="flex"
        flexDirection="column"
        gap="1"
        alignItems="flex-end"
        style={{
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        {/* 日付 */}
        <Text
          fontSize="10px"
          color="Text.highEmphasis"
          width="100%"
          textAlign="start"
        >
          {formattedLabel}
        </Text>

        {/* 預託金（＋履行保証）セクション */}
        <Box
          backgroundColor="Surface.positiveMid"
          borderLeftWidth="2px"
          borderLeftStyle="solid"
          borderLeftColor="Border.positiveMid"
          padding="1"
          width="100%"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        >
          {isHybrid ? (
            <>
              <TooltipRow
                label={t(
                  'component:features.margin_report.tooltip.total_credit_amount',
                )}
                value={formatValue(totalCreditAmount)}
                alignItems="center"
                gap="4"
                labelColor="Text.highEmphasis"
              />
              <Flex
                gap="4"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Text
                  fontSize="11px"
                  color={COLOR_TEXT_MID}
                  fontWeight="300"
                  whiteSpace="nowrap"
                  flexShrink="0"
                >
                  {t('component:features.margin_report.tooltip.breakdown')}
                </Text>
                <Flex flexDirection="column" gap="2px" alignItems="flex-start">
                  <TooltipRow
                    label={t(
                      'component:features.margin_report.tooltip.deposits',
                    )}
                    value={formatValue(deposits)}
                    alignItems="center"
                    gap="1"
                    labelWeight="300"
                  />
                  <TooltipRow
                    label={t(
                      'component:features.margin_report.tooltip.performance_guarantee',
                    )}
                    value={formatValue(performanceGuarantee)}
                    alignItems="center"
                    gap="1"
                    labelWeight="300"
                  />
                </Flex>
              </Flex>
            </>
          ) : (
            <TooltipRow
              label={t('component:features.margin_report.tooltip.deposits')}
              value={formatValue(deposits)}
              alignItems="center"
              labelColor="Text.highEmphasis"
            />
          )}
        </Box>

        {/* 必要証拠金セクション */}
        <Flex paddingLeft="3" alignItems="center" width="100%">
          {/* 縦線 */}
          <Box
            height="100%"
            width="0px"
            borderLeftWidth="1px"
            borderLeftStyle="solid"
            borderLeftColor="Border.neutralMid"
          />

          <Flex
            flexDirection="column"
            gap="1"
            alignItems="flex-start"
            minHeight="1px"
            minWidth="1px"
          >
            {/* 必要証拠金 */}
            <Flex
              alignItems="center"
              width="100%"
              borderLeftWidth="2px"
              borderLeftStyle="dotted"
              borderLeftColor="Border.midEmphasis"
            >
              <Box
                height="1px"
                width="8px"
                backgroundColor="Border.midEmphasis"
                flexShrink="0"
              />
              <Box
                backgroundColor="Surface.negativeMid"
                borderLeftWidth="2px"
                borderLeftStyle="solid"
                borderLeftColor="Border.negativeMid"
                padding="4px 8px"
                flex="1"
                display="flex"
                flexDirection="column"
                gap="2px"
                alignItems="flex-end"
                justifyContent="center"
                minHeight="1px"
                minWidth="1px"
              >
                <TooltipRow
                  label={t(
                    'component:features.margin_report.tooltip.required_margin',
                  )}
                  value={formatValue(requiredMargin)}
                  alignItems="center"
                  labelWeight="300"
                  valueColor="Text.negativeMid"
                />
                <Flex
                  gap="4"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Text
                    fontSize="11px"
                    color={COLOR_TEXT_MID}
                    fontWeight="300"
                    whiteSpace="nowrap"
                    flexShrink="0"
                  >
                    {t('component:features.margin_report.tooltip.of_which')}
                  </Text>
                  <Flex
                    flexDirection="column"
                    gap="2px"
                    alignItems="flex-start"
                  >
                    <TooltipRow
                      label={t(
                        'component:features.margin_report.tooltip.initial_margin',
                      )}
                      value={formatValue(initialMargin)}
                      gap="1"
                      labelWeight="300"
                    />
                    <TooltipRow
                      label={t(
                        'component:features.margin_report.tooltip.unsettled_amount',
                      )}
                      value={formatUnsettledAmount(unsettledAmount)}
                      gap="1"
                      labelWeight="300"
                      borderBottomWidth="1px"
                      borderBottomColor="Border.extraHighEmphasis"
                    />
                    <TooltipRow
                      label={t(
                        'component:features.margin_report.tooltip.subtotal',
                      )}
                      value={formatValue(subtotal)}
                      gap="1"
                      labelWeight="300"
                    />
                  </Flex>
                </Flex>
              </Box>
            </Flex>

            {/* 不足 */}
            <OutlinedRow
              label={t('component:features.margin_report.tooltip.shortage')}
              value={formatValue(shortage)}
              valueColor={
                shortage != null && shortage > 0
                  ? 'Text.negativeMid'
                  : 'Text.highEmphasis'
              }
            />

            {/* 余剰金（返金可能） */}
            <OutlinedRow
              label={t(
                'component:features.margin_report.tooltip.surplus_refundable',
              )}
              value={formatValue(refundableSurplus)}
            />
          </Flex>
        </Flex>
      </Box>
    </Flex>
  )
}
